import { useSearchParams } from 'marketplace-ui-common/hooks/useSearchParams';
import { useCallback, useMemo } from 'react';
import { generatePath, useLocation } from 'quartz-routing/react-router';
import { Paths } from '../../constants/paths';
import { useFilterConfig } from '../../filters/hooks/useFilterConfig';
import { useProductTypeSearchParam } from '../../hooks/useProductTypeSearchParams';
import { useCollectionQuery } from './useCollectionQuery';
import { useSearchResultsPagination } from './useSearchResultsPagination';
export function useSearchResultsCanonicalPaths() {
  const {
    pathname
  } = useLocation();
  const collectionResult = useCollectionQuery();
  const productType = useProductTypeSearchParam();
  const searchParams = useSearchParams();
  const {
    currentPage,
    pageCount
  } = useSearchResultsPagination();
  const {
    AGENT_TEAM,
    CATEGORY,
    OBJECTIVE
  } = useFilterConfig();
  const getCanonicalPaths = useCallback((rootPath, pagePath, routeParams = {}) => {
    return {
      current: pagePath && currentPage > 1 ? generatePath(pagePath, Object.assign({
        page: currentPage
      }, routeParams)) : generatePath(rootPath, routeParams),
      next: pagePath && currentPage < pageCount ? generatePath(pagePath, Object.assign({
        page: currentPage + 1
      }, routeParams)) : undefined,
      previous: pagePath && currentPage > 2 ? generatePath(pagePath, Object.assign({
        page: currentPage - 1
      }, routeParams)) : currentPage === 2 ? generatePath(rootPath, routeParams) : undefined
    };
  }, [currentPage, pageCount]);
  return useMemo(() => {
    if (collectionResult.data && !collectionResult.isUmbrellaCollection) {
      return getCanonicalPaths(Paths.FEATURED_COLLECTION, undefined, {
        collectionSlug: collectionResult.data.slug
      });
    }
    if (productType === 'APP') {
      const categoryValues = CATEGORY ? searchParams.getList(CATEGORY.field) : [];
      if (categoryValues.length === 1) {
        const categoryOption = CATEGORY === null || CATEGORY === void 0 ? void 0 : CATEGORY.options.find(option => option.value === categoryValues[0]);
        if (categoryOption && categoryOption.slug) {
          return getCanonicalPaths(Paths.APPS_CATEGORY, Paths.APPS_CATEGORY_PAGE, {
            categorySlug: categoryOption.slug
          });
        }
      }
      return getCanonicalPaths(Paths.APPS_ALL, Paths.APPS_ALL_PAGE);
    }
    if (productType === 'AGENT') {
      const agentTeamValues = AGENT_TEAM ? searchParams.getList(AGENT_TEAM.field) : [];
      if (agentTeamValues.length === 1) {
        const agentTeamOption = AGENT_TEAM === null || AGENT_TEAM === void 0 ? void 0 : AGENT_TEAM.options.find(option => option.value === agentTeamValues[0]);
        if (agentTeamOption && agentTeamOption.slug) {
          return getCanonicalPaths(Paths.AGENTS_TEAM, Paths.AGENTS_TEAM_PAGE, {
            teamSlug: agentTeamOption.slug
          });
        }
      }
      return getCanonicalPaths(Paths.AGENTS_ALL, Paths.AGENTS_ALL_PAGE);
    }
    if (productType === 'WORKFLOW_TEMPLATE') {
      const objectiveValues = OBJECTIVE ? searchParams.getList(OBJECTIVE.field) : [];
      if (objectiveValues.length === 1) {
        const objectiveOption = OBJECTIVE === null || OBJECTIVE === void 0 ? void 0 : OBJECTIVE.options.find(option => option.value === objectiveValues[0]);
        if (objectiveOption !== null && objectiveOption !== void 0 && objectiveOption.slug) {
          return getCanonicalPaths(Paths.WORKFLOW_TEMPLATES_OBJECTIVE, Paths.WORKFLOW_TEMPLATES_OBJECTIVE_PAGE, {
            objectiveSlug: objectiveOption.slug
          });
        }
      }
      return getCanonicalPaths(Paths.WORKFLOW_TEMPLATES_ALL, Paths.WORKFLOW_TEMPLATES_ALL_PAGE);
    }
    if (pathname === Paths.SEARCH_RESULTS_SOLUTIONS_PARTNERS) {
      return getCanonicalPaths(Paths.SOLUTIONS_PARTNERS_ALL, undefined);
    }
    return getCanonicalPaths(Paths.SEARCH_RESULTS, Paths.SEARCH_RESULTS_PAGE);
  }, [AGENT_TEAM, CATEGORY, OBJECTIVE, collectionResult, getCanonicalPaths, pathname, productType, searchParams]);
}